// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { invoiceDetailSchema } from '@/billing/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

const updateInvoiceResponseSchema = z.object({
  data: invoiceDetailSchema,
  metadata: responseMetadataSchema,
});

// Fields omitted from the wire request are left untouched on the server
// (matches the sparse _do_update pattern). Explicit `null` on a nullable
// field clears the stored value; explicit `null` on a NOT NULL field
// (invoiceDate, billFromName) is rejected with a 400.
export interface UpdateInvoiceInput {
  invoiceKey: string;
  invoiceNumber?: string;
  customerKey?: string | null;
  invoiceDate?: DateTime;
  dueDate?: DateTime | null;
  paymentTerms?: string | null;
  billFromName?: string;
  billFromAddress?: string | null;
  billToCompanyName?: string | null;
  billingContactName?: string | null;
  billToAddress?: string | null;
  billToPhone?: string | null;
  billToEmail?: string | null;
  purchaseOrderNumber?: string | null;
  memo?: string | null;
  paymentInstructions?: string | null;
  notes?: string | null;
}

export async function updateInvoice(input: UpdateInvoiceInput) {
  const { invoiceKey, ...body } = input;
  const url = `/v1/invoice/${invoiceKey}`;
  const response = await api.patch(url, {
    ...body,
    invoiceDate: serializeDateForAPI(input.invoiceDate),
    dueDate: serializeDateForAPI(input.dueDate),
  });

  const result = await parseResponse(
    updateInvoiceResponseSchema,
    response.data,
    {
      schemaName: 'UpdateInvoice',
      endpoint: url,
    }
  );
  return result.data;
}
